import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CheckboxComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { TranslatePipe, TranslationService } from '../../../../core/i18n';
import { CONSENT_AGREEMENT_PDF_URL } from './review-terms.constants';

/**
 * Terms and Conditions consent checkbox and legal copy on Review & Pay.
 */
@Component({
  selector: 'app-review-terms',
  imports: [CheckboxComponent, TranslatePipe, TypographyComponent],
  templateUrl: './review-terms.html',
  styleUrl: './review-terms.scss',
})
export class ReviewTerms {
  private readonly translation = inject(TranslationService);

  readonly accepted = input.required<FormControl<boolean>>();
  /** When true, shows the blocking agreement-required message under the copy. */
  readonly agreementError = input(false);
  /** Emitted when the customer opens Terms and Conditions from the inline link. */
  readonly termsLinkClicked = output<void>();

  /**
   * Mirrors the FormControl for [checked] binding.
   * puro-checkbox is OnPush and writeValue does not markForCheck, so CVA-only updates stay stale.
   */
  protected readonly isAccepted = signal(false);

  protected readonly consentPdfUrl = computed(() => {
    this.translation.revision();
    return this.translation.locale() === 'fr'
      ? CONSENT_AGREEMENT_PDF_URL.fr
      : CONSENT_AGREEMENT_PDF_URL.en;
  });

  constructor() {
    effect((onCleanup) => {
      const control = this.accepted();
      this.isAccepted.set(control.value);
      const sub = control.valueChanges.subscribe((value) => {
        this.isAccepted.set(value);
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  /**
   * Keeps the shared FormControl in sync when the customer toggles the checkbox.
   */
  protected onCheckedChange(checked: boolean): void {
    this.accepted().setValue(checked);
  }

  /**
   * Opens the Terms and Conditions modal from the inline hyperlink.
   */
  protected onTermsLinkClick(event: Event): void {
    event.preventDefault();
    this.termsLinkClicked.emit();
  }
}
